import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { map, defer, from, catchError, throwError, switchMap } from 'rxjs';
import * as i2 from '@polaris-customerweb/sharedcomponents.config';
import { WEB_COMPONENT_HTTP_INTERCEPTORS } from '@polaris-customerweb/sharedcomponents.core';

class TokenService {
    constructor() {
        this.token = '';
    }
    setToken(token) {
        this.token = token;
    }
    getToken() {
        return Promise.resolve(this.token);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "20.3.13", ngImport: i0, type: TokenService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "20.3.13", ngImport: i0, type: TokenService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "20.3.13", ngImport: i0, type: TokenService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class AuthService {
    constructor(tokenService, configService) {
        this.tokenService = tokenService;
        // breaking-change: [RC-30653] - remove token from config service
        if (configService.auth.token) {
            tokenService.setToken(configService.auth.token);
        }
    }
    /**
     * Get an observable that emits true if there is a valid auth token stored, or false if not. This observable emits
     * once and completes.
     */
    isAuthenticated$() {
        return this.getToken$().pipe(map((token) => Boolean(token)));
    }
    /**
     * Get the stored auth token if it is valid.
     * @returns An observable that emits the stored auth token. This observable emits once and completes.
     */
    getToken$() {
        return defer(() => from(this.tokenService
            .getToken()
            .then((token) => {
            const validation = this.validateToken(token);
            return validation.valid ? token : null;
        })
            .catch(() => null)));
    }
    validateToken(token) {
        if (!token) {
            return { expired: false, valid: false };
        }
        const expTime = this.getTokenExpirationTime(token);
        if (!expTime) {
            return { expired: false, valid: false };
        }
        if (expTime * 1000 <= Date.now()) {
            return { expired: true, valid: true };
        }
        return { expired: false, valid: true };
    }
    /**
     * Retrieves the expiration time for a JWT
     * @param token the JWT
     * @returns the expiration timestamp in seconds or undefined if an error occurs
     */
    getTokenExpirationTime(token) {
        if (typeof token !== 'string') {
            return null;
        }
        const tokenString = token.split('.')[1];
        if (!tokenString) {
            return null;
        }
        let expiryInSeconds;
        try {
            const parsed = JSON.parse(window.atob(tokenString));
            expiryInSeconds = parsed?.exp ?? null;
        }
        catch {
            expiryInSeconds = null;
        }
        return expiryInSeconds;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "20.3.13", ngImport: i0, type: AuthService, deps: [{ token: TokenService }, { token: i2.ConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "20.3.13", ngImport: i0, type: AuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "20.3.13", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: TokenService }, { type: i2.ConfigService }] });

class AuthInterceptor {
    constructor(authService) {
        this.authService = authService;
        this.cvcMicroserviceRegex = /^https:\/\/(.*).polariscvc(-dev|-stage)?.com\/.*$/;
        this.etcServiceRegex = /etc\.polaris\.com/;
    }
    intercept(req, next) {
        if (this.cvcMicroserviceRegex.test(req.url) || this.etcServiceRegex.test(req.url)) {
            if (req.params.get('skipAuthorization')) {
                const clone = req.clone({
                    params: req.params.delete('skipAuthorization'),
                });
                return next.handle(clone);
            }
            return this.authService.getToken$().pipe(catchError((error) => {
                return throwError(() => error);
            }), switchMap((token) => {
                const clone = req.clone({
                    headers: req.headers.set('Authorization', `Bearer ${token ?? ''}`),
                });
                return next.handle(clone);
            }));
        }
        return next.handle(req);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "20.3.13", ngImport: i0, type: AuthInterceptor, deps: [{ token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "20.3.13", ngImport: i0, type: AuthInterceptor, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "20.3.13", ngImport: i0, type: AuthInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: AuthService }] });

function provideAuthInterceptor() {
    return [
        {
            multi: true,
            provide: WEB_COMPONENT_HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
        },
    ];
}

/**
 * Generated bundle index. Do not edit.
 */

export { AuthInterceptor, AuthService, TokenService, provideAuthInterceptor };
